// pdfCompressor.js - JavaScript interop file for Blazor WebAssembly with Webpack
import PDFCompressor from './PDFCompressor';

// Create a DotNet namespace for Blazor interop
window.PDFCompressorInterop = {
    /**
     * Compresses a PDF file from an input element
     * @param {string} inputId - The ID of the input element containing the PDF file
     * @param {number} quality - Image quality (0.1 to 1.0)
     * @param {number} imageResolution - Resolution for images in DPI
     * @param {boolean} grayscale - Convert images to grayscale
     * @returns {Promise<string>} - A promise that resolves with the compressed file data as base64
     */
    async compressPdfFromInput(inputId, quality, imageResolution, grayscale) {
        try {
            const inputElement = document.getElementById(inputId);
            if (!inputElement || !inputElement.files || inputElement.files.length === 0) {
                throw new Error('No file selected or input element not found');
            }

            const file = inputElement.files[0];
            if (!file.type.includes('pdf')) {
                throw new Error('Selected file is not a PDF');
            }

            // Show file info in console
            console.log(`Compressing PDF: ${file.name} (${PDFCompressor.formatFileSize(file.size)})`);

            // Compress the PDF
            const compressedPdf = await PDFCompressor.compressPDF(file, {
                quality: quality,
                imageResolution: imageResolution,
                grayscale: grayscale
            });

            // Calculate size reduction
            const reduction = PDFCompressor.getReductionPercentage(file.size, compressedPdf.size);
            console.log(`Compression complete: ${PDFCompressor.formatFileSize(compressedPdf.size)} (${reduction}% reduction)`);

            // Convert to base64
            return await blobToBase64(compressedPdf);
        } catch (error) {
            console.error('Error in compressPdfFromInput:', error);
            throw error;
        }
    },

    /**
     * Compresses a PDF file from base64 string
     * @param {string} base64Data - The PDF file as a base64 encoded string
     * @param {number} quality - Image quality (0.1 to 1.0)
     * @param {number} imageResolution - Resolution for images in DPI
     * @param {boolean} grayscale - Convert images to grayscale
     * @returns {Promise<string>} - A promise that resolves with the compressed file data as base64
     */
    async compressPdfFromBase64(base64Data, quality, imageResolution, grayscale) {
        try {
            // Convert base64 to blob
            const binaryString = atob(base64Data.split(',')[1] || base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes.buffer], { type: 'application/pdf' });

            // Show file info in console
            console.log(`Compressing PDF from base64 (${PDFCompressor.formatFileSize(blob.size)})`);

            // Compress the PDF
            const compressedPdf = await PDFCompressor.compressPDF(blob, {
                quality: quality,
                imageResolution: imageResolution,
                grayscale: grayscale
            });

            // Calculate size reduction
            const reduction = PDFCompressor.getReductionPercentage(blob.size, compressedPdf.size);
            console.log(`Compression complete: ${PDFCompressor.formatFileSize(compressedPdf.size)} (${reduction}% reduction)`);

            // Convert back to base64
            return await blobToBase64(compressedPdf);
        } catch (error) {
            console.error('Error in compressPdfFromBase64:', error);
            throw error;
        }
    },

    /**
     * Saves the compressed PDF to the user's device
     * @param {string} base64Data - The PDF file as a base64 encoded string
     * @param {string} fileName - The name to use for the downloaded file
     */
    saveAs(base64Data, fileName) {
        try {
            // Create a download link
            const link = document.createElement('a');
            link.href = base64Data.startsWith('data:') ? base64Data : `data:application/pdf;base64,${base64Data}`;
            link.download = fileName || 'compressed.pdf';

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error in saveAs:', error);
            throw error;
        }
    }
};

/**
 * Converts a Blob to a base64 string
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} - A promise that resolves with the base64 string
 */
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Export a function that initializes the interop
export function initPDFCompressorInterop() {
    // This function will be called by the Blazor app to ensure the module is loaded
    console.log('PDF Compressor interop initialized');
    return true;
}

